import React, { useState, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { StyledTable } from '../../components';
import { useResolutionScale } from '../../hooks';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import {
  fetchQualityDefectDetails,
  closeDefect,
} from '../../redux/actionCreators/qualityDefect';
import { markComplete } from '../../redux/actionCreators/printBarcode';
import { useRedirectToNextScreen } from '../../hooks';
import { OPERATIONS, REDIRECT_URLS, DARK_MODE , isPuneJlr } from '../../constants';
import TableActionBar, {
  GradientButton,
} from '../../components/TableActionBar/TableActionBar';
import DefectModal from '../../components/ReportModal/DefectModal';
import CloseDefectModal from '../../components/ReportModal/CloseDefectModal';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TakeOut from '../../components/TakeOut/TakeOut';
import MoveToMr from '../../components/MoveToMr/MoveToMr';
import styles from './QualityandDefect.module.scss';

const mapStateToProps = (state) => ({
  currentLineItem: state.station.currentLineItem,
  partValidation: state.partValidation,
  receivePart: state.receivePart,
  themeMode: state.theme.themeMode,
  user: state.user,
  allowedOperations: state.operations.allowedOperations,
});

const mapDispatchToProps = (dispatch) => ({
  fetchQualityDefectDetails: (vin, success, error) =>
    dispatch(fetchQualityDefectDetails(vin, success, error)),
  closeDefect: (defectIds, success, error) =>
    dispatch(closeDefect(defectIds, success, error)),
  markComplete: (success, error, seq) =>
    dispatch(markComplete(success, error, seq)),
});

const QualityandDefect = ({
  currentLineItem,
  partValidation,
  receivePart,
  themeMode,
  fetchQualityDefectDetails,
  closeDefect,
  markComplete,
  user,
  allowedOperations,
}) => {
  const { scaleValue } = useResolutionScale();
  const { showMessageOverlay } = useMessageContextManager();
  const redirect = useRedirectToNextScreen();

  const [manualOpenDefects, setManualOpenDefects] = useState([]);
  const [manualClosedDefects, setManualClosedDefects] = useState([]);
  const [autoOpenDefects, setAutoOpenDefects] = useState([]);
  const [autoClosedDefects, setAutoClosedDefects] = useState([]);
  const [activeDefectTab, setActiveDefectTab] = useState('manual');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [isDefectModalOpen, setIsDefectModalOpen] = useState(false);
  const [isCloseDefectModalOpen, setIsCloseDefectModalOpen] = useState(false);
  const [closingDefects, setClosingDefects] = useState(false);
  const [selectedDefectIds, setSelectedDefectIds] = useState([]);
  const [fetchedAsnFromQuality, setFetchedAsnFromQuality] = useState('');

  const serialNumber =
    partValidation.serialNumber ||
    receivePart.serialNumber ||
    currentLineItem?.serialNumber;

  const sequenceNumber =
    partValidation?.sequenceNumber || currentLineItem?.sequenceNumber ||  fetchedAsnFromQuality;

  const allOpsInterlock =
    [OPERATIONS.MACHINE, OPERATIONS.QUALITY, OPERATIONS.TAKE_IN_OUT].every(
      (op) => allowedOperations?.includes(op),
    );

  // ✅ Fixed normalizeDefectRow — stationUser = createdBy, checkedBy = mesCheckedByUser ?? defectRaisedBy
  const normalizeDefectRow = (d, status, defectCategory) => {
		const rawId = d?.mesDefectId ?? d?.defectId ?? d?.id;

		if (rawId === undefined || rawId === null) return null;

		const id = Number(rawId);
		if (Number.isNaN(id)) return null;

		const defectOpenedTs =
			d?.mesDefectTs ?? d?.defectOpenedTs ?? d?.openedTs ?? null;

		// Keep skip and close information separate
		const skipReason = d?.mesSkipReason ?? d?.skipReason ?? '';
		const skipRemark = d?.mesSkipRemark ?? d?.skipRemark ?? '';

		const closeReason = d?.mesCloseReason ?? d?.closeReason ?? '';
		const closeRemark = d?.mesCloseRemark ?? d?.closeRemark ?? '';

		const stationUser = d?.createdBy ?? '';
		const checkedBy = d?.mesCheckedByUser ?? d?.defectRaisedBy ?? '';

		const defectTypeLabel = defectCategory === 'MANUAL' ? 'Manual' : 'Auto';

		const parameterKey =
			d?.machineDefect?.parameterKey ?? d?.partDefect?.parameterKey ?? '-';

		return {
			...d,
			id,
			defectOpenedTs,

			// Separate fields for display
			skipReason: skipReason || '-',
			skipRemark: skipRemark || '-',
			closeReason: closeReason || '-',
			closeRemark: closeRemark || '-',

			stationUser: stationUser || '-',
			checkedBy: checkedBy || '-',
			parameterKey: parameterKey || '-',
			defectType: defectTypeLabel,
			defectStatus: status,
			isManual: defectCategory === 'MANUAL',
		};
	};

  const normalizeDefectList = (list, status, defectCategory) =>
    (list || [])
      .map((d) => normalizeDefectRow(d, status, defectCategory))
      .filter(Boolean);

  const fetchDefects = () => {
    if (!serialNumber) return;
    setLoading(true);

    fetchQualityDefectDetails(
      serialNumber,
      (data) => {
        setFetchedAsnFromQuality(data?.asn || ''); 
        const manualOpen = normalizeDefectList(
          data?.open?.manualDefects,
          'OPEN',
          'MANUAL',
        );
        const manualClosed = normalizeDefectList(
          data?.closed?.manualDefects,
          'CLOSED',
          'MANUAL',
        );

        const autoOpen = [
          ...normalizeDefectList(data?.open?.partDefects, 'OPEN', 'AUTO'),
          ...normalizeDefectList(data?.open?.machineDefects, 'OPEN', 'AUTO'),
        ];
        const autoClosed = [
          ...normalizeDefectList(data?.closed?.partDefects, 'CLOSED', 'AUTO'),
          ...normalizeDefectList(data?.closed?.machineDefects, 'CLOSED', 'AUTO'),
        ];

        setManualOpenDefects(manualOpen);
        setManualClosedDefects(manualClosed);
        setAutoOpenDefects(autoOpen);
        setAutoClosedDefects(autoClosed);
        setSelectedDefectIds([]);
        setLoading(false);
      },
      (err) => {
        setFetchedAsnFromQuality(''); 
        console.error('Error fetching defects:', err);
        showMessageOverlay({
          contextText: 'Fetch Failed',
          info: err?.response?.data?.message || 'Failed to load defects',
          error: true,
        });
        setLoading(false);
      },
    );
  };

  useEffect(() => {
    if (serialNumber) {
      fetchDefects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber]);

  const handleTabChange = (_event, newValue) => {
    setActiveDefectTab(newValue);
    setSelectedDefectIds([]);
    setStatusFilter('ALL');
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setSelectedDefectIds([]);
  };

  const handleCloseDefect = () => {
    if (selectedDefectIds.length === 0) {
      showMessageOverlay({
        contextText: 'No Defects Selected',
        info: 'Please select at least one defect to close.',
        error: true,
      });
      return;
    }
    setIsCloseDefectModalOpen(true);
  };

  const handleSubmitCloseDefects = ({ reason, remarks }) => {
    if (!reason?.trim()) {
      showMessageOverlay({
        contextText: 'Reason Required',
        info: 'Please enter a reason to close defect(s).',
        error: true,
      });
      return;
    }
    if (selectedDefectIds.length === 0) {
      showMessageOverlay({
        contextText: 'No Defects Selected',
        info: 'Please select at least one defect to close.',
        error: true,
      });
      return;
    }

    setClosingDefects(true);
    const closeOverlay = showMessageOverlay({
      contextText: 'Closing Defects...',
      loading: true,
    });

    const payload = selectedDefectIds.map((id) => ({
      defectId: id,
      closeReason: reason.trim(),
      closeRemark: (remarks || '').trim(),
      closedBy: user?.username || user?.name || '',
    }));

    closeDefect(
      payload,
      (res) => {
        setClosingDefects(false);
        setIsCloseDefectModalOpen(false);
        closeOverlay();
        showMessageOverlay({
          contextText: 'Success',
          info: res?.message || 'Defects closed successfully',
          success: true,
        });
        setSelectedDefectIds([]);
        fetchDefects();
      },
      (err) => {
        setClosingDefects(false);
        closeOverlay();
        showMessageOverlay({
          contextText: 'Close Defect Failed',
          info: err?.response?.data?.message || 'An error occurred',
          error: true,
        });
      },
    );
  };

  const handleMarkComplete = () => {
    const closeOverlay = showMessageOverlay({
      contextText: 'Marking Complete...',
      loading: true,
    });

    markComplete(
      () => {
        closeOverlay();
        showMessageOverlay({
          contextText: 'Station Completed Successfully',
          success: true,
        });
        setTimeout(() => {
          redirect();
        }, 2000);
      },
      (err) => {
        closeOverlay();
        showMessageOverlay({
          contextText: 'Mark Complete Failed',
          info: err?.response?.data?.message || 'An error occurred',
          error: true,
        });
      },
      sequenceNumber,
    );
  };

  const redirectToHomeScreen = () =>
    redirect(null, {
      operation: OPERATIONS.RECEIVE_PART,
      redirectUrl: REDIRECT_URLS[OPERATIONS.RECEIVE_PART],
    });

  const partDetailsColumns = [
    {
      title: 'Scanned Serial No./VIN',
      field: 'serialNumber',
      width: 300,
      render: (row) => (
        <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {row.serialNumber}
        </Typography>
      ),
    },
    {
      title: 'KSPEC',
      field: 'partNumber',
      width: 250,
      render: (row) => (
        <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {row.partNumber}
        </Typography>
      ),
    },
    {
      title: isPuneJlr() ? 'Model' : 'KSPEC Description',
      field: isPuneJlr() ? 'model' : 'description',
      width: 450,
      render: (row) => (
        <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {isPuneJlr() ? (currentLineItem?.model || row.description) : row.description}
        </Typography>
      ),
    },
    {
      title:'Lot Number',
      field: 'lotNumber',
      width: 250,
      render: (row) => (
        <Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
          {currentLineItem?.lotNumber || row.lotNumber}
        </Typography>
      ),
    },
  ];

  const partDetailsData = [
    {
      serialNumber: serialNumber || '-',
      partNumber:
        currentLineItem?.partNumber || currentLineItem?.vcPartNumber || '-',
      description:
        currentLineItem?.description || currentLineItem?.vcDescription || '-',
      lotNumber:
        currentLineItem?.lotNumber || '-',
    },
  ];

  // ✅ Station User = createdBy, Checked By = mesCheckedByUser ?? defectRaisedBy
  const manualDefectColumns = [
		{
			title: 'Station User',
			field: 'stationUser',
			align: 'center',
			width: 150,
		},
		{ title: 'Type', field: 'defectType', align: 'center', width: 150 },
		{
			title: 'Open Reason',
			field: 'skipReason',
			align: 'center',
			width: 220,
      render: (row) => (
				<Typography
					sx={{
						display: '-webkit-box',
						WebkitBoxOrient: 'vertical',
						WebkitLineClamp: 3,
						overflow: 'hidden',
						whiteSpace: 'normal',
						overflowWrap: 'break-word',
						wordBreak: 'break-word',
						lineHeight: 1.4,
					}}
				>
					{row.skipReason}
				</Typography>
			),
		},
		{
			title: 'Open Remark',
			field: 'skipRemark',
			align: 'center',
			width: 250,
      render: (row) => (
				<Typography
					sx={{
						display: '-webkit-box',
						WebkitBoxOrient: 'vertical',
						WebkitLineClamp: 3,
						overflow: 'hidden',
						whiteSpace: 'normal',
						overflowWrap: 'break-word',
						wordBreak: 'break-word',
						lineHeight: 1.4,
					}}
				>
					{row.skipRemark}
				</Typography>
			),
		},
		{
			title: 'Close Reason',
			field: 'closeReason',
			align: 'center',
			width: 220,
      render: (row) => (
				<Typography
					sx={{
						display: '-webkit-box',
						WebkitBoxOrient: 'vertical',
						WebkitLineClamp: 3,
						overflow: 'hidden',
						whiteSpace: 'normal',
						overflowWrap: 'break-word',
						wordBreak: 'break-word',
						lineHeight: 1.4,
					}}
				>
					{row.closeReason}
				</Typography>
			),
		},
		{
			title: 'Close Remark',
			field: 'closeRemark',
			align: 'center',
			width: 250,
      render: (row) => (
				<Typography
					sx={{
						display: '-webkit-box',
						WebkitBoxOrient: 'vertical',
						WebkitLineClamp: 3,
						overflow: 'hidden',
						whiteSpace: 'normal',
						overflowWrap: 'break-word',
						wordBreak: 'break-word',
						lineHeight: 1.4,
					}}
				>
					{row.closeRemark}
				</Typography>
			),
		},
		// {
		// 	title: 'Defect reason',
		// 	field: 'reason',
		// 	align: 'center',
		// 	width: 250,
		// 	render: (row) => (
		// 		<Typography
		// 			sx={{
		// 				display: '-webkit-box',
		// 				WebkitBoxOrient: 'vertical',
		// 				WebkitLineClamp: 3,
		// 				overflow: 'hidden',
		// 				whiteSpace: 'normal',
		// 				overflowWrap: 'break-word',
		// 				wordBreak: 'break-word',
		// 				lineHeight: 1.4,
		// 			}}
		// 		>
		// 			{row.reason}
		// 		</Typography>
		// 	),
		// },
		// { title: 'Remark', field: 'remark', align: 'center', width: 300 },
		{
			title: 'Status',
			field: 'defectStatus',
			align: 'center',
			width: 150,
			render: (row) => (
				<Box
					sx={{
						background:
							row.defectStatus === 'CLOSED'
								? 'rgba(76, 175, 80, 0.1)'
								: 'rgba(244, 67, 54, 0.1)',
						color: row.defectStatus === 'CLOSED' ? '#4CAF50' : '#F44336',
						px: 2,
						py: 0.5,
						borderRadius: '20px',
						display: 'inline-block',
						fontWeight: 'bold',
					}}
				>
					{row.defectStatus}
				</Box>
			),
		},
		{
			title: 'Checked By',
			field: 'checkedBy',
			align: 'center',
			width: 150,
		},
		{
			title: 'Opened Time',
			field: 'defectOpenedTs',
			align: 'center',
			width: 200,
			render: (row) => (
				<Typography sx={{ fontSize: '0.9rem' }}>
					{row.defectOpenedTs
						? new Date(row.defectOpenedTs).toLocaleString()
						: '-'}
				</Typography>
			),
		},
	];

  const autoDefectColumns = [
    {
      title: 'Station User',
      field: 'stationUser',
      align: 'center',
      width: 150,
    },
    {
      title: 'Parameter Key',
      field: 'parameterKey',
      align: 'center',
      width: 260,
    },
    {
      title: 'Defect reason',
      field: 'reason',
      align: 'center',
      width: 250,
      render: (row) => (
        <Typography
            sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                lineHeight: 1.4,
            }}
        >
            {row.reason}
        </Typography>
      ),
    },
    { title: 'Remark', field: 'remark', align: 'center', width: 300 },
    {
      title: 'Status',
      field: 'defectStatus',
      align: 'center',
      width: 150,
      render: (row) => (
        <Box
          sx={{
            background:
              row.defectStatus === 'CLOSED'
                ? 'rgba(76, 175, 80, 0.1)'
                : 'rgba(244, 67, 54, 0.1)',
            color: row.defectStatus === 'CLOSED' ? '#4CAF50' : '#F44336',
            px: 2,
            py: 0.5,
            borderRadius: '20px',
            display: 'inline-block',
            fontWeight: 'bold',
          }}
        >
          {row.defectStatus}
        </Box>
      ),
    },
    {
      title: 'Checked By',
      field: 'checkedBy',
      align: 'center',
      width: 150,
    },
    {
      title: 'Opened Time',
      field: 'defectOpenedTs',
      align: 'center',
      width: 200,
      render: (row) => (
        <Typography sx={{ fontSize: '0.9rem' }}>
          {row.defectOpenedTs
            ? new Date(row.defectOpenedTs).toLocaleString()
            : '-'}
        </Typography>
      ),
    },
  ];

  const [columnsState, setColumnsState] = useState(manualDefectColumns);

  useEffect(() => {
    setColumnsState(
      activeDefectTab === 'manual' ? manualDefectColumns : autoDefectColumns,
    );
  }, [activeDefectTab]);

  const visibleColumns = useMemo(
    () => columnsState.filter((col) => !col.hidden),
    [columnsState],
  );


  const activeOpenDefects = useMemo(
    () => (activeDefectTab === 'manual' ? manualOpenDefects : autoOpenDefects),
    [activeDefectTab, manualOpenDefects, autoOpenDefects],
  );

  const activeClosedDefects = useMemo(
    () =>
      activeDefectTab === 'manual' ? manualClosedDefects : autoClosedDefects,
    [activeDefectTab, manualClosedDefects, autoClosedDefects],
  );

  const handleSelectionChange = (newSelection) =>
    setSelectedDefectIds(newSelection);

  const statusFilteredRows = useMemo(() => {
    if (statusFilter === 'OPEN') return activeOpenDefects;
    if (statusFilter === 'CLOSED') return activeClosedDefects;
    return [...activeOpenDefects, ...activeClosedDefects];
  }, [statusFilter, activeOpenDefects, activeClosedDefects]);

  const statusFilteredTableRows = useMemo(
    () =>
      statusFilteredRows.map((row) => ({
        ...row,
        showCheckBox:
          row.defectStatus === 'OPEN'
            ? true
            : false,
      })),
    [statusFilteredRows],
  );

  const showCheckboxes = statusFilter !== 'CLOSED';

  return (
    <Box className={styles.qualityDefectPage}>
      {/* Current KSPEC Header */}
      <Box sx={{ mb: 2 }}>
        <StyledTable
          title="Current KSPEC"
          columns={partDetailsColumns}
          rows={partDetailsData}
          forceHeight={scaleValue(100)}
          showFilter={false}
          showColumns={false}
          elevated
        />
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, minHeight: 0, mb: 2 }}>
        <Paper
          elevation={3}
          className={styles.tablePaper}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <TableActionBar
            leftContent={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant="inherit"
                  sx={{
                    fontWeight: 600,
                    fontSize: `${scaleValue(24)}px`,
                    color: themeMode === DARK_MODE ? '#F8F8F8' : '#202020',
                  }}
                >
                  {`Defect Details (${statusFilteredRows.length})`}
                </Typography>
                {selectedDefectIds.length > 0 && (
                  <Box className={styles.selectedCount}>
                    {selectedDefectIds.length} selected
                  </Box>
                )}
              </Box>
            }
            showDefactive={!!serialNumber && !!sequenceNumber}
            onDefectClick={() => setIsDefectModalOpen(true)}
            showRefresh={true}
            onRefresh={fetchDefects}
            showFilter={false}
            showColumns={true}
            showExport={false}
            columns={columnsState}
            rows={statusFilteredRows}
            onColumnChange={setColumnsState}
            rightContent={
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TakeOut
                  partDetails={{
                    serialNumber,
                    sequenceNumber,
                  }}
                  redirectToNextScreen={redirectToHomeScreen}
                  btnStyles={{
                    height: `${scaleValue(54)}px !important`,
                    minHeight: `${scaleValue(54)}px !important`,
                    width: 'max-content !important',
                    minWidth: `${scaleValue(180)}px !important`,
                    fontSize: `${scaleValue(18)}px !important`,
                    background:
                      'linear-gradient(90deg, #CB1D49 0%, #C71616 100%) !important',
                    borderRadius: `${scaleValue(8)}px !important`,
                    padding: `${scaleValue(10)}px ${scaleValue(38)}px !important`,
                    '& svg': { display: 'none !important' },
                  }}
                />
                <MoveToMr
                  partDetails={{
                    serialNumber,
                    sequenceNumber,
                  }}
                  btnStyles={{
                    height: `${scaleValue(54)}px !important`,
                    minHeight: `${scaleValue(54)}px !important`,
                  }}
                />
              </Box>
            }
          />

          <Box
            sx={{
              px: 2,
              pt: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Tabs
              value={activeDefectTab}
              onChange={handleTabChange}
              variant="fullWidth"
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={{
                flex: '1 1 320px',
                minHeight: 48,
                backgroundColor:
                  themeMode === DARK_MODE
                    ? 'rgba(255,255,255,0.06)'
                    : 'rgba(13,71,161,0.06)',
                border: `1px solid ${
                  themeMode === DARK_MODE
                    ? 'rgba(255,255,255,0.15)'
                    : 'rgba(21,101,192,0.25)'
                }`,
                borderRadius: 2,
                p: 0.5,
              }}
            >
              <Tab
                disableRipple
                value="manual"
                label={`Manual (${
                  manualOpenDefects.length + manualClosedDefects.length
                })`}
                sx={{
                  textTransform: 'none',
                  fontWeight: 1000,
                  fontSize: `${scaleValue(16)}px`,
                  borderRadius: 2,
                  color: themeMode === DARK_MODE ? '#F8F8F8' : '#0F107B',
                  '&.Mui-selected': {
                    background:
                      'linear-gradient(90deg, rgba(6,129,74,0.25) 0%, rgba(16,225,137,0.20) 100%)',
                    color: themeMode === DARK_MODE ? '#E6FFEF' : '#06814A',
                  },
                }}
              />
              <Tab
                disableRipple
                value="auto"
                label={`Auto (${
                  autoOpenDefects.length + autoClosedDefects.length
                })`}
                sx={{
                  textTransform: 'none',
                  fontWeight: 1000,
                  fontSize: `${scaleValue(16)}px`,
                  borderRadius: 2,
                  color: themeMode === DARK_MODE ? '#F8F8F8' : '#0F107B',
                  '&.Mui-selected': {
                    background:
                      'linear-gradient(90deg, rgba(33,150,243,0.22) 0%, rgba(33,204,225,0.16) 100%)',
                    color: themeMode === DARK_MODE ? '#E7F6FF' : '#1565C0',
                  },
                }}
              />
            </Tabs>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: `${scaleValue(16)}px`,
                }}
              >
                Status
              </Typography>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  sx={{
                    backgroundColor:
                      themeMode === DARK_MODE
                        ? 'rgba(255,255,255,0.06)'
                        : '#F4F8FF',
                    borderRadius: 2,
                    height: 40,
                    '.MuiSelect-select': {
                      py: 1,
                      fontWeight: 900,
                    },
                  }}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              px: 2,
              pb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <StyledTable
              columns={visibleColumns}
              rows={statusFilteredTableRows}
              showFilter={false}
              showColumns={false}
              showCheckBox={showCheckboxes}
              selectall={showCheckboxes}
              selectedRows={selectedDefectIds}
              onSelectionChange={handleSelectionChange}
              maxHeight="52vh"
              fillHeight
            />
          </Box>
        </Paper>
      </Box>

      {/* Footer */}
      <Box className={styles.footer}>
        <Grid container alignItems="center" justifyContent="flex-end">
          <Grid item sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {selectedDefectIds.length > 0 && (
              <Typography
                sx={{
                  color: themeMode === DARK_MODE ? '#90CAF9' : '#1565C0',
                  fontWeight: 600,
                  fontSize: `${scaleValue(16)}px`,
                }}
              >
                {selectedDefectIds.length} defect(s) selected
              </Typography>
            )}

            {statusFilter !== 'CLOSED' &&
              (activeDefectTab === 'manual' ? manualOpenDefects : autoOpenDefects).length > 0 && (
                <GradientButton
                  onClick={handleCloseDefect}
                  gradient="linear-gradient(90deg, #06814A 0%, #10E189 100%)"
                  sx={{ height: 56, borderRadius: '12px' }}
                  disabled={selectedDefectIds.length === 0}
                >
                  <CheckCircleOutlineIcon sx={{ mr: 1 }} />
                  Close Defect
                </GradientButton>
              )}
            <GradientButton
              onClick={handleMarkComplete}
              gradient="linear-gradient(90deg, #2196F3 0%, #21CCE1 100%)"
              sx={{ height: 56, borderRadius: '12px' }}
              disabled={!sequenceNumber}
            >
              <CheckCircleOutlineIcon sx={{ mr: 1 }} />
              Mark Complete
            </GradientButton>
          </Grid>
        </Grid>
      </Box>

      <DefectModal
        open={isDefectModalOpen}
        handleClose={() => setIsDefectModalOpen(false)}
        partDetailsRow={partDetailsData.map((row) => ({
          serialNumber: row.serialNumber,
          parentPartNumber: row.partNumber,
          parentPartDescription: row.description,
        }))}
        partDetails={{
          serialNumber,
          sequenceNumber,
        }}
        onDefectCreated={fetchDefects}
      />

      <CloseDefectModal
        open={isCloseDefectModalOpen}
        onClose={() => setIsCloseDefectModalOpen(false)}
        onSubmit={handleSubmitCloseDefects}
        submitting={closingDefects}
        selectedCount={selectedDefectIds.length}
      />
    </Box>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(QualityandDefect);